/**
 * Main app component with routing and providers
 */

import { AcceptVisitPage } from '@ox/visits-ui/components/pages/AcceptVisitPage';
import { AgentDashboardPage } from '@ox/visits-ui/components/pages/AgentDashboardPage';
import { ErrorPage } from '@ox/visits-ui/components/pages/ErrorPage';
import { ExpiredPage } from '@ox/visits-ui/components/pages/ExpiredPage';
import { FormPage } from '@ox/visits-ui/components/pages/FormPage';
import { LandingPage } from '@ox/visits-ui/components/pages/LandingPage';
import { GuidancePage } from '@ox/visits-ui/components/pages/GuidancePage';
import { VisitFormRedirectPage } from '@ox/visits-ui/components/pages/VisitFormRedirectPage';
import { FeaturesBanner } from '@ox/visits-ui/components/shared/FeaturesBanner';
import { Header } from '@ox/visits-ui/components/shared/Header';
import { MockModeBanner } from '@ox/visits-ui/components/shared/MockModeBanner';
import { AuthProvider, useAuth } from '@ox/visits-ui/context/AuthContext';
import { FormProvider } from '@ox/visits-ui/context/FormContext';
import { LocationProvider, Route, Router } from 'preact-iso';
import { ImageGalleryPage } from '@ox/visits-ui/components/pages/ImageGalleryPage';
import { VideoPage } from '@ox/visits-ui/components/pages/VideoPage';

/**
 * Protected form page with header and form provider
 */
function ProtectedFormPage() {
  const { isAuthenticated, isLoading, error } = useAuth();
  if (isLoading) {
    return (
      <div className="container" style={{ textAlign: 'center', marginTop: '100px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated || error) {
    return <ErrorPage />;
  }

  return (
    <>
      <Header />
      <FormProvider>
        <FormPage />
      </FormProvider>
    </>
  );
}

export function App() {
  return (
    <LocationProvider>
      <AuthProvider>
        <MockModeBanner />
        <FeaturesBanner />

        <Router>
          {/* Public landing page */}
          <Route path="/" component={LandingPage} />

          {/* Agent dashboard - task queue for field agents */}
          <Route path="/dashboard" component={AgentDashboardPage} />

          {/* Token expired page */}
          <Route path="/expired" component={ExpiredPage} />

          {/* Public visit acceptance page - no authentication required */}
          <Route path="/visits/:visitRequestId/accept" component={AcceptVisitPage} />

          {/* Protected routes - require authentication */}
          <Route path="/form" component={ProtectedFormPage} />

          {/* Public guidance page - no authentication required */}
          <Route path="/guidance" component={GuidancePage} />

          {/* Public prewalk image gallery page - no authentication required */}
          <Route path="/image-gallery/:visitFormId" component={ImageGalleryPage} />

          {/* Public prewalk video page - no authentication required */}
          <Route path="/video/:visitFormId" component={VideoPage} />

          {/* Visit Form Redirect Page */}
          <Route path="/visit-form/:visitFormId" component={VisitFormRedirectPage} />

          {/* Default route */}
          <Route default component={LandingPage} />

        </Router>
      </AuthProvider>
    </LocationProvider>
  );
}
